import { Button, Callout, Card, Field, Input } from '@provenance/design';
import {
  VerificationResult,
  normaliseVerificationCode,
  verifyRequestSchema,
  type VerifyResponse,
} from '@provenance/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  HelpCircle,
  RefreshCcw,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { PublicApiError, publicRequest } from '../api';

/**
 * ════════════════════════════════════════════════════════════════════════════
 * PUBLIC CERTIFICATE VERIFICATION — spec §11
 *
 * The page that gives the electronic stamp its practical value. A customs officer in
 * Jakarta, a bank in Shanghai, or an importer anywhere types a number and a code and
 * finds out whether the document in front of them is genuine and current.
 *
 * Four things this page has to get right, and they are all about the reader rather than
 * about us:
 *
 *  NO ACCOUNT, NO FRICTION. Two fields. Nothing to sign up for, nothing to install. If
 *  verifying were even slightly annoying, nobody would do it and the stamp would be
 *  decorative.
 *
 *  THE ANSWER IS UNMISSABLE. Someone is deciding whether to release a shipment. Valid,
 *  Revoked and Superseded are given entirely different treatments — not three shades of
 *  the same badge.
 *
 *  IT WORKS ON A PHONE, IN A PORT. Single column, large type, no horizontal scroll.
 *
 *  IT DISCLOSES ALMOST NOTHING. Document type, chamber, exporter, date, status. Not the
 *  invoice values, not the consignee, not the goods. A verifier needs to know it is real;
 *  they have no business seeing the exporter's commercial terms.
 * ════════════════════════════════════════════════════════════════════════════
 */
export function Verify() {
  const [params, setParams] = useSearchParams();
  const [result, setResult] = useState<VerifyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const form = useForm({
    resolver: zodResolver(verifyRequestSchema),
    defaultValues: {
      certificateNo: params.get('no') ?? '',
      verificationCode: params.get('code') ?? '',
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    setResult(null);
    try {
      const response = await publicRequest<VerifyResponse>('/api/v1/public/verify', {
        method: 'POST',
        body: {
          certificateNo: values.certificateNo.trim(),
          // The code is printed on paper and typed by a human, so 0/O and 1/I confusions
          // are normalised rather than rejected.
          verificationCode: normaliseVerificationCode(values.verificationCode),
        },
      });
      setResult(response);
      // Shareable and refreshable — someone verifying on a colleague's behalf can send
      // the link rather than the numbers.
      setParams({ no: values.certificateNo.trim(), code: values.verificationCode.trim() });
    } catch (err) {
      setError(
        err instanceof PublicApiError
          ? err.status === 429
            ? 'Too many attempts from this connection. Wait a minute and try again.'
            : err.message
          : 'Could not reach the verification service. Check your connection and try again.',
      );
    }
  });

  return (
    <div className="mx-auto w-full max-w-[44rem] px-4 py-10 sm:py-16">
      <header>
        <div className="flex items-center gap-2.5">
          <span className="grid size-9 place-items-center rounded-[var(--pv-radius)] bg-[var(--pv-ink)] [font-size:15px] font-bold text-[var(--pv-leaf)]">
            P
          </span>
          <span className="[font-size:16px] font-bold tracking-[-0.02em]">Provenir</span>
        </div>

        <p className="pv-eyebrow mt-8">Certificate verification</p>
        <h1 className="pv-display mt-2">
          Is this certificate genuine?
        </h1>
        <p className="pv-lede mt-4">
          Enter the certificate number and the short verification code printed beside it. This
          works for any certificate of origin issued through Provenir by an Australian Chamber
          of Commerce.
        </p>
      </header>

      <Card className="mt-8">
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
          <Field
            label="Certificate number"
            required
            error={form.formState.errors.certificateNo?.message}
            help="As printed on the certificate, usually in the top-right box."
          >
            <Input
              mono
              autoFocus
              placeholder="260099"
              autoComplete="off"
              spellCheck={false}
              className="![font-size:16px]"
              {...form.register('certificateNo')}
            />
          </Field>

          <Field
            label="Verification code"
            required
            error={form.formState.errors.verificationCode?.message}
            help="Six characters, printed next to the certificate number. Letter case does not matter."
          >
            <Input
              mono
              placeholder="YEHEDB"
              autoComplete="off"
              spellCheck={false}
              maxLength={12}
              className="![font-size:16px] uppercase"
              {...form.register('verificationCode')}
            />
          </Field>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={form.formState.isSubmitting}
            leadingIcon={<Search className="size-4" />}
            className="mt-1 w-full"
          >
            Verify this certificate
          </Button>
        </form>
      </Card>

      {error && (
        <Callout tone="stop" className="mt-5" icon={<AlertTriangle className="size-4" />}>
          {error}
        </Callout>
      )}

      {result && <VerifyResultCard result={result} />}

      <Card className="mt-6 !bg-transparent !border-dashed">
        <p className="pv-eyebrow text-[var(--pv-faint)]">Why there are two fields</p>
        <p className="mt-2 [font-size:13px] leading-relaxed text-[var(--pv-muted)]">
          Certificate numbers run in sequence. If the number alone were enough, anyone could
          count upwards and read off every certificate a chamber has ever issued, along with the
          exporters' names. The verification code printed beside it is what prevents that — which
          is why a wrong code and a number that does not exist give you exactly the same answer.
        </p>
      </Card>
    </div>
  );
}

/**
 * The verdict, in a live region.
 *
 * Submitting the form replaces nothing and navigates nowhere — the answer simply appears
 * further down the page. A sighted person sees it; a screen reader user is left on the
 * submit button with no indication that anything happened at all (WCAG 4.1.3, found in
 * the T-082 pass, which measured zero live regions on every public page).
 *
 * `polite` rather than `assertive`: the result is important but it is also exactly what
 * the person just asked for, so it should be announced when the reader reaches a natural
 * pause rather than cutting off whatever it is mid-word.
 */
function VerifyResultCard({ result }: { result: VerifyResponse }) {
  if (result.result === VerificationResult.NOT_FOUND) {
    return (
      <Card role="status" aria-live="polite" className="mt-5 !border-[var(--pv-warn-line)] !bg-[var(--pv-warn-bg)]">
        <div className="flex items-start gap-3">
          <HelpCircle aria-hidden className="mt-0.5 size-6 shrink-0 text-[var(--pv-warn)]" />
          <div className="min-w-0">
            <h2 className="[font-size:var(--pv-text-h2)] font-bold text-[var(--pv-warn)]">
              No certificate matches
            </h2>
            <p className="mt-2 [font-size:14px] leading-relaxed text-[var(--pv-ink-soft)]">
              Either that number was never issued through Provenir, or the verification code
              does not match it. Check both against the printed certificate — a transcription
              error is by far the most likely explanation.
            </p>
            <p className="mt-3 [font-size:13px] leading-relaxed text-[var(--pv-muted)]">
              If you are confident the certificate is real and the details are right, contact the
              issuing chamber directly. Not every Australian chamber issues through Provenir.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const certificate = result.certificate!;
  const valid = result.result === VerificationResult.VALID;
  const revoked = result.result === VerificationResult.REVOKED;

  return (
    <Card
      role="status"
      aria-live="polite"
      className={
        valid
          ? 'mt-5 !border-[var(--pv-ok-line)] !bg-[var(--pv-ok-bg)]'
          : 'mt-5 !border-[var(--pv-stop-line)] !bg-[var(--pv-stop-bg)]'
      }
    >
      {/* The verdict, given the whole top of the card. Someone is deciding whether to
          release a shipment on this. */}
      <div className="flex items-start gap-3">
        {valid ? (
          <CheckCircle2 aria-hidden className="mt-0.5 size-7 shrink-0 text-[var(--pv-ok)]" />
        ) : revoked ? (
          <Ban aria-hidden className="mt-0.5 size-7 shrink-0 text-[var(--pv-stop)]" />
        ) : (
          <RefreshCcw aria-hidden className="mt-0.5 size-7 shrink-0 text-[var(--pv-stop)]" />
        )}
        <div className="min-w-0">
          <h2
            className={
              valid
                ? '[font-size:1.4rem] font-bold leading-tight tracking-[-0.02em] text-[var(--pv-ok)]'
                : '[font-size:1.4rem] font-bold leading-tight tracking-[-0.02em] text-[var(--pv-stop)]'
            }
          >
            {valid
              ? 'This certificate is genuine and current'
              : revoked
                ? 'This certificate has been revoked'
                : 'This certificate has been superseded'}
          </h2>
          {result.status?.note && (
            <p className="mt-2 [font-size:14px] font-medium leading-relaxed text-[var(--pv-ink-soft)]">
              {result.status.note}
            </p>
          )}
          {result.status?.supersededByCertificateNo && (
            <p className="mt-1.5 [font-size:14px] text-[var(--pv-ink-soft)]">
              The current version is{' '}
              <strong className="pv-mono">{result.status.supersededByCertificateNo}</strong>. Ask
              the exporter for it.
            </p>
          )}
        </div>
      </div>

      <dl className="mt-5 grid gap-x-8 border-t border-[rgba(20,48,30,0.1)] pt-4 sm:grid-cols-2">
        <Row label="Certificate number" value={certificate.certificateNo} mono />
        <Row label="Certificate date" value={formatDate(certificate.certificateDate)} />
        <Row label="Document type" value={certificate.documentTypeName} />
        <Row label="Agreement" value={certificate.agreement ?? 'Non-preferential'} />
        <Row label="Exporter" value={certificate.exporterName} />
        <Row label="Destination" value={countryName(certificate.destinationCountry)} />
      </dl>

      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-[rgba(20,48,30,0.1)] pt-4">
        {certificate.issuingChamber.logoUrl ? (
          <img
            src={certificate.issuingChamber.logoUrl}
            alt=""
            className="h-8 max-w-[160px] object-contain"
          />
        ) : (
          <ShieldCheck aria-hidden className="size-5 text-[var(--pv-ink-soft)]" />
        )}
        <div className="min-w-0 flex-1">
          <p className="pv-eyebrow text-[var(--pv-faint)]">Issued by</p>
          <p className="[font-size:13.5px] font-semibold">{certificate.issuingChamber.name}</p>
        </div>
        {certificate.issuingChamber.websiteUrl && (
          <Button variant="secondary" size="sm" asChild>
            <a href={certificate.issuingChamber.websiteUrl} target="_blank" rel="noreferrer">
              Contact the chamber
            </a>
          </Button>
        )}
      </div>

      <p className="mt-4 [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
        Verified {new Date(result.verifiedAt).toLocaleString('en-AU')}. Only the details shown
        above are disclosed — the shipment's commercial terms are not published.
      </p>
    </Card>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="border-b border-[rgba(20,48,30,0.08)] py-2 last:border-b-0">
      <dt className="[font-size:var(--pv-text-xs)]  text-[var(--pv-faint)]">
        {label}
      </dt>
      <dd className={`m-0 mt-0.5 [font-size:14px] font-medium ${mono ? 'pv-mono' : ''}`}>{value}</dd>
    </div>
  );
}

/**
 * A readable country name from an ISO alpha-2 code.
 *
 * Intl.DisplayNames rather than a bundled table: it costs nothing, covers every country,
 * and the raw code is a reasonable fallback — someone reading a certificate understands
 * "VN" perfectly well.
 */
const regionNames =
  typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en-AU'], { type: 'region' })
    : null;

function countryName(code: string): string {
  if (!code) return '—';
  try {
    return regionNames?.of(code.toUpperCase()) ?? code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
}

/**
 * A certificate date is a CALENDAR date, read in UTC.
 *
 * This page is the public record, reachable from anywhere. Local getters would show a
 * certificate dated 23 September as the 22nd to a verifier in London, against a PDF that
 * plainly reads 23/09/2026 — the one discrepancy a verification page must never produce.
 */
function formatDate(iso: string): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : `${String(date.getUTCDate()).padStart(2, '0')}/${String(date.getUTCMonth() + 1).padStart(2, '0')}/${date.getUTCFullYear()}`;
}
